import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { BULK_DETACH_PARENT_MUTATION } from '@/modules/API/parent/parentAssociationHandler'
import { useMutation } from '@apollo/client'
import { FunctionComponent, useState } from 'react'
import styles from './bulkDissociateFamiliesDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { useNotification } from '@/modules/context/NotificationContext'

type BulkDissociateParentsDialogProps = {
    isDialogOpen: boolean
    handleCloseDialog: () => void
    selectedParentIds: number[]
    daycareId: number
    onSuccess: () => void
}

const BulkDissociateParentsDialog: FunctionComponent<BulkDissociateParentsDialogProps> = ({
    isDialogOpen,
    handleCloseDialog,
    selectedParentIds,
    daycareId,
    onSuccess
}) => {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const [bulkDissociateParents] = useMutation(BULK_DETACH_PARENT_MUTATION)

    async function handleConfirm(): Promise<void> {
        if (isSubmitting || selectedParentIds.length === 0) return

        setIsSubmitting(true)

        try {
            await bulkDissociateParents({
                variables: {
                    daycare_id: daycareId,
                    parent_ids: selectedParentIds
                }
            })

            showNotificationSuccess(t('bulkDissociationParentsSuccess'))
            onSuccess()
            handleCloseDialog()
        } catch {
            showNotificationError(t('bulkDissociateParentsError'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog
            open={isDialogOpen}
            handleClose={isSubmitting ? undefined : handleCloseDialog}
            className={styles.Dialog}
        >
            <DialogHeader title={t('bulkDissociateFamilies')} />
            <DialogBody className={styles.DialogBody}>
                <div>
                    <p className={styles.Text}>
                        {selectedParentIds.length}{' '}
                        {selectedParentIds.length > 1 ? t('familiesSelected') : t('familySelected')}
                    </p>
                    <p className={styles.Text}> {t('confirmBulkDissociateFamilies')}</p>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={handleConfirm}
                        disabled={isSubmitting || selectedParentIds.length === 0}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'cancel'}
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default BulkDissociateParentsDialog
